import { supabase } from "@/utils/supabase/client";

const createSpaceStore = (set, get) => ({
  spaces: [],
  spaceContent: {}, // Cache for individual space content, keyed by slug
  modifiedSpaces: new Set(), // Track which spaces have been modified

  // Add spaces management
  setSpaces: (spaces) => set({ spaces }),

  clearSpaces: () => set({ spaces: [] }),

  // Mark a space as modified (needs refresh)
  markSpaceAsModified: (slug) => {
    set((state) => {
      const modifiedSpaces = new Set(state.modifiedSpaces);
      modifiedSpaces.add(slug);
      return { modifiedSpaces };
    });
  },

  // Clear modified status for a space
  clearModifiedStatus: (slug) => {
    set((state) => {
      const modifiedSpaces = new Set(state.modifiedSpaces);
      modifiedSpaces.delete(slug);
      return { modifiedSpaces };
    });
  },

  // Check if a space needs refreshing
  doesSpaceNeedRefresh: (slug) => {
    return get().modifiedSpaces.has(slug);
  },

  // Get space content from cache or fetch if needed
  getSpaceContent: async (slug, forceRefresh = false) => {
    const store = get();
    const cachedContent = store.spaceContent[slug];
    const needsRefresh =
      forceRefresh || store.doesSpaceNeedRefresh(slug) || !cachedContent;

    if (needsRefresh) {
      try {
        const session = get().session;
        const userId = session?.user?.id || null;

        const { data: spaceContent, error: spaceError } = await supabase.rpc(
          "get_space_content",
          {
            space_slug: slug,
            user_id: userId,
            impersonate: false,
          }
        );

        if (spaceError || !spaceContent?.[0]?.space_data) {
          throw spaceError || new Error("No space data returned");
        }

        // Store in cache
        const spaceData = spaceContent[0];
        set((state) => ({
          spaceContent: {
            ...state.spaceContent,
            [slug]: spaceData,
          },
        }));

        // Clear modified flag
        store.clearModifiedStatus(slug);

        return spaceData;
      } catch (error) {
        console.error("Error fetching space content:", error);
        return null;
      }
    }

    return cachedContent;
  },

  // Cache space content directly
  cacheSpaceContent: (slug, content) => {
    set((state) => ({
      spaceContent: {
        ...state.spaceContent,
        [slug]: content,
      },
    }));
  },

  // Fetch user spaces
  fetchUserSpaces: async () => {
    try {
      const session = get().session;
      const userId = session?.user?.id;

      if (!userId) {
        set({ spaces: [] });
        return;
      }

      const { data: spaces, error } = await supabase
        .from("spaces")
        .select("id, title, slug, created_at")
        .eq("user_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      set({ spaces: spaces || [] });

      return spaces;
    } catch (error) {
      console.error("Error fetching spaces:", error);
      get().showToast({
        title: "Error",
        description: "Failed to load your spaces",
        variant: "destructive",
      });
      return [];
    }
  },

  // Create initial space (with validated session)
  createInitialSpace: async ({
    slug,
    type,
    tags = [],
    expirationHours,
    referralCode,
    templateId,
    templateConfig,
    links,
    media,
    preGeneratedSpaceId,
    validatedSession = null, // Accept a pre-validated session
  }) => {
    try {
      // Use the validated session if provided, otherwise get current session
      let session = validatedSession;

      if (!session) {
        console.log(
          "🔍 No validated session provided, getting current session"
        );
        const {
          data: { session: currentSession },
        } = await supabase.auth.getSession();
        session = currentSession;
      } else {
        console.log("✅ Using pre-validated session for space creation");
      }

      if (!session) {
        throw new Error("No active session for space creation");
      }

      // Check if session is expired and attempt refresh
      if (session.expires_at && session.expires_at < Date.now() / 1000) {
        console.log("🔄 Session expired, attempting refresh...");
        const { data: refreshData, error: refreshError } =
          await supabase.auth.refreshSession();

        if (refreshError || !refreshData.session) {
          console.error("❌ Session refresh failed:", refreshError);
          throw new Error(
            "Session expired and refresh failed. Please sign in again."
          );
        }

        session = refreshData.session;
        console.log("✅ Session refreshed successfully");
      }

      // Double-check we have a valid access token
      if (!session.access_token) {
        console.error("❌ No access token in session");
        throw new Error(
          "Invalid session - no access token. Please sign in again."
        );
      }

      console.log(
        "Creating space with raw links:",
        Array.isArray(links) ? links.length : "none",
        Array.isArray(links) && links.length > 0 ? links.slice(0, 2) : []
      );

      // Ensure links are properly formatted
      const formattedLinks = Array.isArray(links)
        ? links.map((link) => {
            // Handle string links
            if (typeof link === "string") {
              return {
                url: link,
                metadata: {
                  url: link,
                  title: new URL(link).hostname.replace("www.", ""),
                },
              };
            }
            // Handle object links
            if (typeof link === "object" && link.url) {
              return {
                url: link.url,
                metadata: link.metadata || {
                  url: link.url,
                  title: new URL(link.url).hostname.replace("www.", ""),
                },
              };
            }
            return link;
          })
        : [];

      // Prepare the payload
      const payload = {
        slug,
        spaceType: type || "individual",
        tags,
        expirationHours,
        // Include template information and content
        templateId,
        templateConfig,
        links: formattedLinks,
        media,
        preGeneratedSpaceId,
      };

      console.log("Sending to create-space:", {
        slug,
        tagsCount: tags.length,
        hasTemplateId: Boolean(templateId),
        hasTemplateConfig: Boolean(templateConfig),
        linksCount: formattedLinks.length,
        mediaCount: media?.length || 0,
        preGeneratedSpaceId,
      });

      // Log detailed link information
      if (formattedLinks.length > 0) {
        console.log(
          "Formatted links for create-space:",
          formattedLinks.map((link) => ({
            url: link.url,
            hasMetadata: Boolean(link.metadata),
            title: link.metadata?.title || "No title",
          }))
        );
      }

      // Call the edge function
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/create-space`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const error = await response.json();

        // If space creation failed and we have a referral code, clean up the referral
        if (referralCode) {
          try {
            // Call the clean-up endpoint to remove the referral
            await fetch(
              `${process.env.NEXT_PUBLIC_SUPABASE_URL}/functions/v1/cleanup-referral`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${session.access_token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  referralCode,
                  userId: session.user.id,
                }),
              }
            );
          } catch (cleanupError) {
            console.error("Error cleaning up referral:", cleanupError);
          }
        }

        throw new Error(error.message || "Failed to create space");
      }

      const data = await response.json();

      // Update local state with the new space
      set((state) => ({
        spaces: [
          ...(state.spaces || []),
          {
            id: data.space_id,
            slug,
            type,
            draft_id: data.draft_id,
          },
        ],
      }));

      return {
        success: true,
        spaceId: data.space_id,
        draftId: data.draft_id,
      };
    } catch (error) {
      console.error("Error creating space:", error);
      return {
        success: false,
        error: error.message || "Failed to create space",
      };
    }
  },

  // Create space
  createSpace: async ({ slug, type, tags, title }) => {
    try {
      const session = get().session;
      if (!session) throw new Error("Not authenticated");

      const userId = session.user.id;

      // Call the create_space function
      const { data, error } = await supabase.rpc("create_space", {
        p_user_id: userId,
        p_slug: slug,
        p_title: title || `My ${type} Space`,
        p_tags: tags,
      });

      if (error) throw error;

      // Fetch updated spaces list
      await get().fetchUserSpaces();

      get().showToast({
        title: "Success",
        description: "Space created successfully",
        variant: "success",
      });

      return {
        success: true,
        spaceId: data[0].space_id,
        draftId: data[0].draft_id,
        slug,
        error: null,
      };
    } catch (error) {
      get().showToast({
        title: "Error",
        description: error.message || "Failed to create space",
        variant: "destructive",
      });
      return { success: false, spaceId: null, error: error.message };
    }
  },

  // Fetch all spaces for admin dashboard
  fetchAllSpaces: async () => {
    try {
      const session = get().session;
      if (!session?.user?.id) return [];

      // Call the admin function to get all spaces
      const { data: spaces, error } = await supabase.rpc(
        "admin_get_all_spaces"
      );

      if (error) throw error;

      return spaces || [];
    } catch (error) {
      console.error("Error fetching all spaces:", error);
      return [];
    }
  },

  // Fetch all users for admin dashboard
  fetchAllUsers: async () => {
    try {
      const session = get().session;
      if (!session?.user?.id) return [];

      // Call the admin function to get all users
      const { data: users, error } = await supabase.rpc("admin_get_all_users");

      if (error) throw error;

      return users || [];
    } catch (error) {
      console.error("Error fetching all users:", error);
      return [];
    }
  },

  // Admin function to update space qonnect exclusion
  updateSpaceQonnectExclusion: async (spaceId, isQonnectExcluded) => {
    try {
      const session = get().session;
      if (!session?.user?.id) {
        throw new Error("Not authenticated");
      }

      // Call the admin function to update qonnect exclusion
      const { data: success, error } = await supabase.rpc(
        "admin_update_space_qonnect_excluded",
        {
          p_space_id: spaceId,
          p_is_qonnect_excluded: isQonnectExcluded,
        }
      );

      if (error) throw error;

      return { success: success, error: null };
    } catch (error) {
      console.error("Error updating space qonnect exclusion:", error);
      return { success: false, error: error.message };
    }
  },
});

export default createSpaceStore;
